import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { API_LINK } from '../../apiLink/apiLink';

const LibraryCategories = ({ onNavigate }) => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Add form
    const [showAddForm, setShowAddForm] = useState(false);
    const [addForm, setAddForm] = useState({ name: '', description: '' });
    const [addLoading, setAddLoading] = useState(false);
    const [addError, setAddError] = useState('');

    // Edit
    const [editId, setEditId] = useState(null);
    const [editForm, setEditForm] = useState({ name: '', description: '' });
    const [editLoading, setEditLoading] = useState(false);
    const [editError, setEditError] = useState('');

    const token = sessionStorage.getItem('token') || localStorage.getItem('token');

    const fetchCategories = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await fetch(`${API_LINK}/api/library/categories`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) setCategories(data.data.categories);
            else setError(data.message || 'Failed to fetch categories');
        } catch {
            setError('Could not connect to server');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { fetchCategories(); }, [fetchCategories]);

    const handleAdd = async (e) => {
        e.preventDefault();
        setAddLoading(true);
        setAddError('');
        try {
            const res = await fetch(`${API_LINK}/api/library/categories`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(addForm)
            });
            const data = await res.json();
            if (data.success) {
                setAddForm({ name: '', description: '' });
                setShowAddForm(false);
                fetchCategories();
            } else setAddError(data.message || 'Failed to create category');
        } catch {
            setAddError('Could not connect to server');
        } finally {
            setAddLoading(false);
        }
    };

    const openEdit = (cat) => {
        setEditId(cat.id);
        setEditForm({ name: cat.name, description: cat.description || '' });
        setEditError('');
    };

    const handleEdit = async (e) => {
        e.preventDefault();
        setEditLoading(true);
        setEditError('');
        try {
            const res = await fetch(`${API_LINK}/api/library/categories/${editId}`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(editForm)
            });
            const data = await res.json();
            if (data.success) {
                setEditId(null);
                fetchCategories();
            } else setEditError(data.message || 'Failed to update category');
        } catch {
            setEditError('Could not connect to server');
        } finally {
            setEditLoading(false);
        }
    };

    const handleDelete = async (id, name) => {
        if (!window.confirm(`Delete category "${name}"?`)) return;
        try {
            const res = await fetch(`${API_LINK}/api/library/categories/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) fetchCategories();
            else alert(data.message || 'Failed to delete');
        } catch {
            alert('Could not connect to server');
        }
    };

    const inputStyle = {
        width: '100%', padding: '10px 12px', border: '1px solid #ddd',
        borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', marginTop: '4px'
    };

    return (
        <Layout role="librarian" onNavigate={onNavigate} activePage="library-categories" pageTitle="Book Categories">
            <div style={{ padding: '20px' }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
                    <div>
                        <h2 style={{ color: '#333', margin: 0 }}>🏷️ Book Categories</h2>
                        <p style={{ color: '#666', margin: '4px 0 0', fontSize: '14px' }}>{categories.length} categories</p>
                    </div>
                    <button
                        onClick={() => { setShowAddForm(f => !f); setAddError(''); }}
                        style={{ backgroundColor: '#238548', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}
                    >
                        {showAddForm ? '✕ Cancel' : '+ Add Category'}
                    </button>
                </div>

                {/* Add Form */}
                {showAddForm && (
                    <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 10px rgba(0,0,0,0.07)', marginBottom: '2rem', border: '2px solid #e8f5e9' }}>
                        <h3 style={{ margin: '0 0 1rem', color: '#238548', fontSize: '16px' }}>➕ New Category</h3>
                        {addError && <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px', marginBottom: '1rem', color: '#dc2626', fontSize: '13px' }}>⚠️ {addError}</div>}
                        <form onSubmit={handleAdd}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: '1rem', alignItems: 'end', flexWrap: 'wrap' }}>
                                <div>
                                    <label style={{ fontSize: '13px', fontWeight: '500', color: '#555' }}>Category Name *</label>
                                    <input
                                        type="text"
                                        required
                                        placeholder="e.g. Computer Science"
                                        value={addForm.name}
                                        onChange={e => setAddForm(p => ({ ...p, name: e.target.value }))}
                                        style={inputStyle}
                                    />
                                </div>
                                <div>
                                    <label style={{ fontSize: '13px', fontWeight: '500', color: '#555' }}>Description (optional)</label>
                                    <input
                                        type="text"
                                        placeholder="Brief description of this category"
                                        value={addForm.description}
                                        onChange={e => setAddForm(p => ({ ...p, description: e.target.value }))}
                                        style={inputStyle}
                                    />
                                </div>
                                <button
                                    type="submit"
                                    disabled={addLoading}
                                    style={{ backgroundColor: '#238548', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', whiteSpace: 'nowrap', height: '42px', marginTop: '22px' }}
                                >
                                    {addLoading ? 'Saving...' : '✓ Save'}
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* Error */}
                {error && (
                    <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px 16px', marginBottom: '1rem', color: '#dc2626', fontSize: '14px' }}>
                        ⚠️ {error}
                    </div>
                )}

                {/* Categories Table */}
                <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ backgroundColor: '#238548', color: 'white' }}>
                                <th style={{ padding: '14px 16px', textAlign: 'left', width: '60px' }}>#</th>
                                <th style={{ padding: '14px 16px', textAlign: 'left' }}>Category Name</th>
                                <th style={{ padding: '14px 16px', textAlign: 'left' }}>Description</th>
                                <th style={{ padding: '14px 16px', textAlign: 'center', width: '150px' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="4" style={{ padding: '40px', textAlign: 'center', color: '#666' }}>⏳ Loading...</td></tr>
                            ) : categories.length === 0 ? (
                                <tr><td colSpan="4" style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                                    No categories yet. Add your first category above.
                                </td></tr>
                            ) : (
                                categories.map((cat, idx) => (
                                    <tr key={cat.id} style={{ borderBottom: '1px solid #eee', backgroundColor: idx % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                                        <td style={{ padding: '14px 16px', color: '#999', fontWeight: '500' }}>{idx + 1}</td>
                                        <td style={{ padding: '14px 16px' }}>
                                            {editId === cat.id ? (
                                                <input
                                                    type="text"
                                                    value={editForm.name}
                                                    onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))}
                                                    style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', width: '100%' }}
                                                />
                                            ) : (
                                                <span style={{ fontWeight: '600', color: '#333' }}>
                                                    <span style={{ backgroundColor: '#e8f5e9', color: '#2e7d32', padding: '4px 10px', borderRadius: '12px', fontSize: '13px' }}>
                                                        🏷️ {cat.name}
                                                    </span>
                                                </span>
                                            )}
                                        </td>
                                        <td style={{ padding: '14px 16px', color: '#666', fontSize: '14px' }}>
                                            {editId === cat.id ? (
                                                <input
                                                    type="text"
                                                    value={editForm.description}
                                                    onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))}
                                                    style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', width: '100%' }}
                                                />
                                            ) : (cat.description || <span style={{ color: '#bbb', fontStyle: 'italic' }}>No description</span>)}
                                        </td>
                                        <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                                            {editId === cat.id ? (
                                                <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                                                    {editError && <span style={{ color: '#dc2626', fontSize: '12px' }}>{editError}</span>}
                                                    <button
                                                        onClick={handleEdit}
                                                        disabled={editLoading}
                                                        style={{ backgroundColor: '#238548', color: 'white', border: 'none', padding: '5px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}
                                                    >
                                                        {editLoading ? '...' : '✓ Save'}
                                                    </button>
                                                    <button
                                                        onClick={() => setEditId(null)}
                                                        style={{ backgroundColor: '#f5f5f5', color: '#555', border: '1px solid #ddd', padding: '5px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            ) : (
                                                <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                                                    <button
                                                        onClick={() => openEdit(cat)}
                                                        style={{ backgroundColor: 'transparent', border: '1px solid #1976d2', color: '#1976d2', padding: '5px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}
                                                    >
                                                        ✏️ Edit
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(cat.id, cat.name)}
                                                        style={{ backgroundColor: 'transparent', border: '1px solid #dc2626', color: '#dc2626', padding: '5px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}
                                                    >
                                                        🗑 Delete
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Quick tip */}
                {!loading && categories.length > 0 && (
                    <div style={{ marginTop: '1rem', padding: '12px 16px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', fontSize: '13px', color: '#166534' }}>
                        💡 These categories appear in the <strong>Book Catalog</strong> dropdown when adding or editing books.
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default LibraryCategories;
